const KEY = "marketlink.errorTrail";
const MAX_ENTRIES = 15;

export type ErrorEntry = {
  type: "js" | "unhandled_rejection" | "api" | "console" | "freeze" | "gps";
  message: string;
  source?: string | null;
  stack?: string | null;
  at: number;
};

function read(): ErrorEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(entries: ErrorEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    /* ignore */
  }
}

function push(entry: ErrorEntry) {
  const entries = read();
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  write(entries);
}

export function getErrorTrail(): ErrorEntry[] {
  return read();
}

// Best-effort send a critical error to /api/log/error (which logs to YOUR
// Cloud Logging). Throttled to avoid flooding on a tight error loop. Uses
// sendBeacon when available so it survives page unload.
const ERROR_REPORT_DEBOUNCE_MS = 2000;
let lastReportAt = 0;
function reportToServer(entry: ErrorEntry) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastReportAt < ERROR_REPORT_DEBOUNCE_MS) return;
  lastReportAt = now;
  try {
    const body = JSON.stringify({
      type: entry.type,
      message: entry.message,
      source: entry.source ?? null,
      stack: entry.stack ?? null,
      path: window.location.pathname,
      ts: entry.at,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/log/error", new Blob([body], { type: "application/json" }));
    } else {
      void fetch("/api/log/error", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      });
    }
  } catch {
    /* swallow — never let error reporting itself raise */
  }
}

export function initErrorCapture() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (e) => {
    const entry: ErrorEntry = {
      type: "js",
      message: String(e.message || e.error || "Unknown error").slice(0, 300),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : null,
      stack: e.error instanceof Error && e.error.stack ? e.error.stack.slice(0, 800) : null,
      at: Date.now(),
    };
    push(entry);
    reportToServer(entry);
  });

  window.addEventListener("unhandledrejection", (e) => {
    const msg =
      e.reason instanceof Error
        ? e.reason.message
        : typeof e.reason === "string"
          ? e.reason
          : JSON.stringify(e.reason);
    const entry: ErrorEntry = {
      type: "unhandled_rejection",
      message: String(msg || "Unhandled rejection").slice(0, 300),
      stack: e.reason instanceof Error && e.reason.stack ? e.reason.stack.slice(0, 800) : null,
      at: Date.now(),
    };
    push(entry);
    reportToServer(entry);
  });

  const origConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    origConsoleError.apply(console, args);
    const msg = args
      .map((a) => {
        if (a instanceof Error) return a.message;
        if (typeof a === "string") return a;
        try { return JSON.stringify(a); } catch { return String(a); }
      })
      .join(" ")
      .slice(0, 300);
    if (msg) {
      push({ type: "console", message: msg, at: Date.now() });
    }
  };
}

// Record a GPS failure so "waiting for GPS signal" reports show the real
// reason (timeout / permission / etc.) in the feedback errorTrail AND in
// Cloud Logging — not just a silent spinner.
export function recordGpsError(message: string) {
  const entry: ErrorEntry = { type: "gps", message: String(message || "gps error").slice(0, 300), at: Date.now() };
  push(entry);
  reportToServer(entry);
}

// Detect main-thread freezes (ANRs throw no JS error, so nothing else catches
// them). A heartbeat that's late by >STALL_MS means the thread was blocked;
// we record the stall + the route and beacon it so it survives an app kill.
export function initFreezeDetector() {
  if (typeof window === "undefined") return;
  const TICK_MS = 2000;
  const STALL_MS = 5000;
  let last = Date.now();
  // Browsers suspend timers while the tab is backgrounded or the phone is
  // locked, so the first tick after resuming would look like a multi-minute
  // "freeze". Reset the clock whenever the page becomes visible and skip
  // measurement while hidden — only real foreground stalls get recorded.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") last = Date.now();
    });
  }
  window.setInterval(() => {
    const now = Date.now();
    const gap = now - last;
    last = now;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    if (gap > STALL_MS) {
      const entry: ErrorEntry = {
        type: "freeze",
        message: `Main thread stalled ~${Math.round(gap)}ms on ${window.location.pathname}`,
        at: now,
      };
      push(entry);
      reportToServer(entry);
    }
  }, TICK_MS);
}

export function captureApiError(url: string, status: number, body: string) {
  push({
    type: "api",
    message: `${status} ${url.slice(0, 100)}: ${body.slice(0, 200)}`,
    at: Date.now(),
  });
}

export const SESSION_EXPIRED_EVENT = "marketlink:session-expired";

function shouldWatchSessionExpiry(url: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return false;
    if (!parsed.pathname.startsWith("/api/")) return false;
    if (parsed.pathname === "/api/auth/user") return false;
    return true;
  } catch {
    return false;
  }
}

export function initSessionExpiryWatcher() {
  if (typeof window === "undefined") return;
  const orig = window.fetch.bind(window);
  window.fetch = async (input: any, init?: any) => {
    const url =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : String(input);
    const res = await orig(input, init);
    if (res.status === 401 && shouldWatchSessionExpiry(url)) {
      try {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      } catch {
        // ignore
      }
    }
    return res;
  };
}
